/*
 * What this tab has on screen, told to the server as `rt.focus` (protocol.md §2.5.1). The server
 * uses it four ways: a conversation somebody is looking at gets no notification hint, a frame that
 * is only worth its bytes to somebody watching — an AI chat's text as Claude writes it — goes only to
 * the tabs showing that chat, the owner of a scope learns who is reading along and who has it open at
 * all, and a document's band shows who is here and who is away (S-01 §3.9). So it has to be true at
 * every moment a frame might be sent, and the rules below are what keep it true:
 *
 * - A page says what it shows (`show`, or `useFocusScope` in a component) and takes it back when it
 *   leaves; the newest page to speak wins, and a page that has been replaced cannot clear its
 *   successor's focus by leaving late. Leaving is reported a moment later, so moving from one chat
 *   to the next is one report and not two.
 * - A hidden tab has nothing on screen, whatever page it is on, but says which scope its page shows,
 *   marked hidden: the server counts that as the scope being open, never as somebody looking (an AI
 *   chat open in a background tab keeps its container a while longer, and gets no live text). The
 *   server knows only while the tab's connection says so, so a page may ask to keep the connection
 *   while hidden (`keepWhileHidden`; `hiddenKeep.ts` bounds how many tabs may, and where). A window
 *   that merely lost focus still shows its page — a chat on a second screen keeps its live text.
 * - **Away is the browser's to judge, and the server only records it** (W4-D2): only the browser
 *   knows whether its window has focus, whether its tab is shown, and how long nobody has touched the
 *   page. A tab is away once its window lost focus or its tab was hidden `presenceGraceMs` ago (30 s,
 *   so a glance at another window fades nobody), or once nobody has given input for `presenceAwayMs`
 *   (10 min); both numbers come from the hello's `Limits`, so the hub and the browser read one. An
 *   away report carries `away`, how long the spell has lasted, and the server turns that into a time
 *   of its own: no client clock is trusted.
 * - **One spell.** Not being here is one unbroken spell from its first sign — the blur, the hide, or
 *   the last input before a long quiet — until focus comes back, the tab is shown again with focus,
 *   or input arrives while the window has focus. A window that lost focus and is then hidden (a click
 *   into a call, then the call going full screen) stays one spell, so no report in between says
 *   "here" and the away time never starts again. Input over a window without focus — the pointer
 *   crossing it on its way elsewhere — ends nothing (W4-D4); a blur into a frame of the page itself
 *   (a rendered block's) is no blur.
 * - A connection starts unfocused on the server. After every catch-up the socket hands the reporter a
 *   way to send (`connected`), and the reporter says again what is on screen; while the socket is
 *   down nothing is sent, and nothing is owed — the next connection is told afresh.
 * - The same report is never sent twice in a row on one connection, and an away report is the same
 *   report however long the spell has lasted: a spell is reported once, when it turns away.
 */

import { useEffect } from "react"

/** A scope as `rt.focus` names it: its kind and its id. */
export interface FocusScope {
	kind: string
	id: number
}

/**
 * What one report says: the scope the page shows, whether the tab is hidden, and — once this spell of
 * not being here counts as away — how many milliseconds it has lasted.
 */
export interface FocusReport extends FocusScope {
	hidden?: true
	away?: number
}

/**
 * A bounded keep: the connection stays for at most `forMs` of one hidden spell, and only in the
 * browser's last keeper place, which any hidden AI chat may take back (`hiddenKeep.ts`) — a document
 * page, whose being open is worth a meeting's length to the faces in its band.
 */
export interface HiddenKeep {
	forMs: number
	lastPlaceOnly: true
}

/** What a page asks beside its scope. */
export interface ShowOptions {
	/**
	 * Keep the tab's connection while it is hidden, past the five minutes after which a hidden tab
	 * closes its socket (`socket.ts`): `true` for a page whose being open matters to the server for
	 * as long as the tab stays — an owner's AI chat, which keeps its container while open — or a
	 * bounded keep (`HiddenKeep`).
	 */
	keepWhileHidden?: boolean | HiddenKeep
}

/** How the page on screen asked to keep its hidden connection, with `true` read as no bound and any place. */
export interface KeepAsk {
	forMs: number
	lastPlaceOnly: boolean
}

/** Sends one report on the current connection; `null` is "nothing on screen". */
export type FocusSink = (report: FocusReport | null) => void

/**
 * The part of the document the reporter reads: whether the tab is visible, and when that changes;
 * whether the window has focus, and which element holds it (a page's own frame is no blur). The two
 * focus readers are optional for a stand-in that has no window: absent, the window has focus.
 */
export interface VisibilitySource {
	readonly visibilityState: DocumentVisibilityState
	hasFocus?(): boolean
	readonly activeElement?: Element | null
	addEventListener(type: "visibilitychange", listener: () => void): void
	removeEventListener(type: "visibilitychange", listener: () => void): void
}

/** The part of the window the reporter reads: its focus and blur, and the person's input. */
export interface WindowSource {
	addEventListener(type: string, listener: () => void, options?: AddEventListenerOptions): void
	removeEventListener(type: string, listener: () => void, options?: EventListenerOptions): void
}

/** The time and the timers the reporter keeps its spells by; a test hands in fake ones. */
export interface PresenceClock {
	/** Milliseconds on a clock that only moves forward: every use is a duration. */
	now(): number
	setTimeout(run: () => void, ms: number): unknown
	clearTimeout(handle: unknown): void
}

/** The browser's: `performance.now()`, which a corrected wall clock cannot set back. */
const browserClock: PresenceClock = {
	now: () => performance.now(),
	setTimeout: (run, ms) => globalThis.setTimeout(run, ms),
	clearTimeout: (handle) => {
		globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>)
	},
}

/**
 * The served limits' defaults (`wire.DefaultLimits`: `presenceGraceMs`, `presenceAwayMs`), used until
 * the first hello names them.
 */
export const PRESENCE_GRACE_MS = 30_000
export const PRESENCE_AWAY_MS = 10 * 60_000

/** The two numbers of the hello's `Limits` the reporter keeps to; a server older than them sends neither. */
export interface PresenceLimits {
	presenceGraceMs?: number
	presenceAwayMs?: number
}

/**
 * The input that shows somebody is at the page: a press, a key, a wheel, a touch, a scroll. Moving
 * the pointer is left out on purpose — it crosses a window on its way to another one (W4-D4).
 */
const INPUTS = ["pointerdown", "keydown", "wheel", "touchstart", "scroll"] as const
const INPUT_OPTIONS: AddEventListenerOptions = { capture: true, passive: true }

function same(a: FocusReport | null, b: FocusReport | null): boolean {
	return (
		a === b ||
		(a !== null &&
			b !== null &&
			a.kind === b.kind &&
			a.id === b.id &&
			a.hidden === b.hidden &&
			(a.away === undefined) === (b.away === undefined))
	)
}

function keepAsk(keep: ShowOptions["keepWhileHidden"]): KeepAsk | null {
	if (keep === true) return { forMs: Infinity, lastPlaceOnly: false }
	if (keep === undefined || keep === false) return null
	return { forMs: keep.forMs, lastPlaceOnly: true }
}

/** The earlier of two moments, either of which may be absent. */
function earlier(a: number | null, b: number | null): number | null {
	if (a === null) return b
	if (b === null) return a
	return Math.min(a, b)
}

export class FocusReporter {
	/** What the newest page to speak shows, and the token of that page's claim. */
	private wanted: FocusScope | null = null
	/** How the page that spoke asked to keep the connection while hidden; null when it did not. */
	private keep: KeepAsk | null = null
	private claim: object | undefined
	private sink: FocusSink | undefined
	/** What the current connection was last told; a new connection has been told nothing, which is null. */
	private sent: FocusReport | null = null
	private readonly doc: VisibilitySource
	private readonly win: WindowSource
	private readonly clock: PresenceClock
	private graceMs = PRESENCE_GRACE_MS
	private awayMs = PRESENCE_AWAY_MS
	/** When the window lost focus, and when the tab was hidden; null while it has focus, or is shown. */
	private blurAt: number | null
	private hiddenAt: number | null
	/** The last input; the page's first moment counts as one. */
	private lastInput: number
	/** Where the unbroken spell of not being here began, null while here; and whether it is away yet. */
	private spellStart: number | null = null
	private spellAway = false
	/** The one timer armed: the end of a grace, or the next look for a long quiet. */
	private timer: unknown = undefined

	constructor(
		doc: VisibilitySource = document,
		win: WindowSource = window,
		clock: PresenceClock = browserClock,
	) {
		this.doc = doc
		this.win = win
		this.clock = clock
		const now = clock.now()
		this.lastInput = now
		this.hiddenAt = doc.visibilityState === "hidden" ? now : null
		this.blurAt = doc.hasFocus?.() === false ? now : null
		doc.addEventListener("visibilitychange", this.onVisibility)
		win.addEventListener("focus", this.onFocus)
		win.addEventListener("blur", this.onBlur)
		for (const type of INPUTS) win.addEventListener(type, this.onInput, INPUT_OPTIONS)
	}

	/**
	 * A page shows `scope` from now on. The returned function takes it back — only if no other page
	 * has spoken since, so a page that unmounts after its successor mounted leaves the successor's
	 * focus alone.
	 */
	show(scope: FocusScope | null, options: ShowOptions = {}): () => void {
		const claim = {}
		this.claim = claim
		this.wanted = scope
		this.keep = scope === null ? null : keepAsk(options.keepWhileHidden)
		this.flush()
		return () => {
			if (this.claim !== claim) return
			this.claim = undefined
			this.wanted = null
			this.keep = null
			// Said a moment later: a page that is replaced leaves just before its successor speaks
			// (React runs the old effect's cleanup first), and the server should hear the successor,
			// not a "nothing" in between that its watchers would be nudged by.
			queueMicrotask(this.flush)
		}
	}

	/** A connection has caught up: it starts unfocused, so say what is on screen now. */
	connected(sink: FocusSink): void {
		this.sink = sink
		this.sent = null
		this.flush()
	}

	/** The connection is gone; the next one is told afresh. */
	disconnected(): void {
		this.sink = undefined
		this.disarm()
	}

	/** The hello's limits: the grace and the quiet after which a tab is away. */
	useLimits(limits: PresenceLimits): void {
		const { presenceGraceMs, presenceAwayMs } = limits
		if (typeof presenceGraceMs === "number" && presenceGraceMs > 0) this.graceMs = presenceGraceMs
		if (typeof presenceAwayMs === "number" && presenceAwayMs > 0) this.awayMs = presenceAwayMs
		this.flush()
	}

	/** Stops following the document and the window, for a test that builds its own reporter. */
	dispose(): void {
		this.disarm()
		this.doc.removeEventListener("visibilitychange", this.onVisibility)
		this.win.removeEventListener("focus", this.onFocus)
		this.win.removeEventListener("blur", this.onBlur)
		for (const type of INPUTS) this.win.removeEventListener(type, this.onInput, INPUT_OPTIONS)
	}

	/** How the page on screen asked to keep the connection while hidden (`ShowOptions`); null when not. */
	keepsWhileHidden(): KeepAsk | null {
		return this.keep
	}

	/**
	 * What the server should hold right now: the page's scope, marked hidden while the tab is, and
	 * with the spell's length once it is away.
	 */
	current(): FocusReport | null {
		if (this.wanted === null) return null
		const now = this.clock.now()
		this.settle(now)
		const report: FocusReport = { ...this.wanted }
		if (this.doc.visibilityState === "hidden") report.hidden = true
		if (this.spellAway && this.spellStart !== null)
			report.away = Math.max(0, Math.round(now - this.spellStart))
		return report
	}

	/**
	 * Brings the spell up to `now`: it starts at the earliest sign of not being here and only ever
	 * reaches back, and it turns away once a blur or a hide in it has outlasted the grace, or the
	 * quiet has lasted `awayMs`. A spell is ended by the events below, never here: with no sign left
	 * there is nothing to end but a spell already over.
	 */
	private settle(now: number): void {
		if (this.doc.visibilityState === "hidden") this.hiddenAt ??= now
		const quiet = now - this.lastInput >= this.awayMs
		const gone = earlier(this.blurAt, this.hiddenAt)
		const start = earlier(gone, quiet ? this.lastInput : null)
		if (start === null) {
			this.endSpell()
			return
		}
		this.spellStart = earlier(this.spellStart, start)
		if (!this.spellAway) this.spellAway = quiet || (gone !== null && now - gone >= this.graceMs)
	}

	private endSpell(): void {
		this.spellStart = null
		this.spellAway = false
	}

	/** Whether the window has focus by the document's own word; a stand-in without one has it. */
	private focused(): boolean {
		return this.doc.hasFocus?.() !== false
	}

	private readonly onVisibility = (): void => {
		const now = this.clock.now()
		if (this.doc.visibilityState === "hidden") {
			this.hiddenAt ??= now
		} else {
			this.hiddenAt = null
			if (this.focused()) this.here(now)
			// Shown in a window that has no focus: the spell goes on as the blur's.
			else this.blurAt ??= now
		}
		this.flush()
	}

	private readonly onFocus = (): void => {
		const now = this.clock.now()
		if (this.doc.visibilityState === "hidden") this.blurAt = null
		else this.here(now)
		this.flush()
	}

	private readonly onBlur = (): void => {
		// Focus moving into a frame of the page — a rendered block's — leaves the person on the page.
		if (this.doc.activeElement?.tagName === "IFRAME") return
		this.blurAt ??= this.clock.now()
		this.flush()
	}

	/**
	 * Input only stores its time — a long quiet is found by the one timer looking — unless it ends a
	 * spell: input in a window with focus, on a tab that is shown, is somebody here.
	 */
	private readonly onInput = (): void => {
		this.lastInput = this.clock.now()
		if (this.spellStart === null || this.blurAt !== null || this.hiddenAt !== null) return
		this.endSpell()
		this.flush()
	}

	/** The person is at the page again: focus is back on a shown tab, which also counts as input. */
	private here(now: number): void {
		this.blurAt = null
		this.lastInput = now
		this.endSpell()
	}

	private readonly flush = (): void => {
		this.disarm()
		if (!this.sink) return
		const next = this.current()
		this.arm()
		if (same(next, this.sent)) return
		this.sent = next
		this.sink(next)
	}

	/**
	 * Arms the one timer the spell needs next: the end of a blur's or a hide's grace, or the moment
	 * the quiet would last `awayMs` — which, finding newer input, looks again from that input. An
	 * away spell needs none: only an event ends it. Nothing is armed without a connection to tell or
	 * a page to speak of.
	 */
	private arm(): void {
		if (!this.sink || this.wanted === null || this.spellAway) return
		const gone = earlier(this.blurAt, this.hiddenAt)
		const quietEnds = this.lastInput + this.awayMs
		const at = gone === null ? quietEnds : Math.min(gone + this.graceMs, quietEnds)
		this.timer = this.clock.setTimeout(this.flush, Math.max(0, at - this.clock.now()))
	}

	private disarm(): void {
		if (this.timer === undefined) return
		this.clock.clearTimeout(this.timer)
		this.timer = undefined
	}
}

/** The tab's one reporter; the socket reports through it after every catch-up. */
export const focusReporter = new FocusReporter()

/**
 * Tells the server that this component's page shows `scope` for as long as it is mounted with it —
 * an AI chat's page, for its chat; a document's, with its bounded keep. `null` shows nothing. The
 * scope is compared by value, so a new object with the same kind and id does not report again; the
 * keep is compared by identity, so a page passes one constant.
 */
export function useFocusScope(
	scope: FocusScope | null,
	reporter: FocusReporter = focusReporter,
	options: ShowOptions = {},
): void {
	const kind = scope?.kind
	const id = scope?.id
	const keep = options.keepWhileHidden
	useEffect(() => {
		if (kind === undefined || id === undefined) return undefined
		return reporter.show({ kind, id }, keep === undefined ? {} : { keepWhileHidden: keep })
	}, [kind, id, reporter, keep])
}
